import * as React from "react";
import NextImage from "next/image";
import { Box, Heading, Stack, Text } from "@chakra-ui/react";

import Button from "components/Chakra/Button";
import CustomLink from "components/Chakra/CustomLink";
import CustomWrapper from "components/Chakra/CustomWrapper";
import useGlobalState from "hooks/useGlobalState";
import ArrowList from "components/ArrowList";
import CityHeader from "components/CityHeader";
import Referral from "components/Referral";
import { Item } from "scenes/PremiumLanding";
import useScrollToId from "hooks/useScrollToId";
import SubHead from "components/SubHead";
import UserBadge from "components/Badges";
import PersonCircle from "components/PersonCircle";
import { FAQS, CCS } from "./FAQS";
import IconCalendar from "components/Chakra/Icons/Calendar";
import IconDottedArrow from "components/Chakra/Icons/DottedArrow";
import IconPerson1 from "components/Chakra/Icons/Person1";
import IconSearchPerson from "components/Chakra/Icons/SearchPerson";

const CheckLanding: React.FC = ({}) => {
  const { user } = useGlobalState();
  const { scrollToId } = useScrollToId();

  return (
    <>
      <CityHeader>
        <IconSearchPerson boxSize="29px" fillWith="ud-primary" />
        <Heading
          as="h1"
          pt={{ base: "20px", md: "5px" }}
          pb={{ base: "50px", md: "21px" }}
          textColor="ud-black"
          lineHeight="46px"
        >
          Prověřte rizikovost kohokoliv
          <br />
          za pár vteřin
        </Heading>
        <Stack
          direction="row"
          pb={{ base: "50px", md: "41px" }}
          spacing={{ base: "20px", md: "30px" }}
        >
          <Stack spacing="5px">
            <Heading as="h2" variant="h3" textAlign="center">
              27 000+
            </Heading>
            <Text textAlign="center" variant="medium" lineHeight="16px">
              prověřených
              <br />
              osob
            </Text>
          </Stack>
          <Box w="1px" h="62px" bgColor="ud-grey-500" />
          <Stack spacing="5px">
            <Heading as="h2" variant="h3" textAlign="center">
              29,5 %
            </Heading>
            <Text textAlign="center" variant="medium" lineHeight="16px">
              z nich mělo
              <br />
              záznam
            </Text>
          </Stack>
          <Box w="1px" h="62px" bgColor="ud-grey-500" />
          <Stack spacing="5px">
            <Heading as="h2" variant="h3" textAlign="center">
              149 Kč
            </Heading>
            <Text textAlign="center" variant="medium" lineHeight="16px">
              za bleskové
              <br />
              prověření
            </Text>
          </Stack>
        </Stack>
        <CustomLink
          variant="button"
          trackEventPayload={{
            "dl.eCat": "cc",
            "dl.eAct": "cc_click",
            "dl.eLab": "landing_cc",
          }}
          to="/provereni-rizikovosti-osob"
        >
          Prověřit osobu
        </CustomLink>
        <Button variant="link" pt="20px" onClick={() => scrollToId("moreCC")}>
          Jak to funguje?
        </Button>
      </CityHeader>
      <CustomWrapper>
        <SubHead title="Proč prověřovat?" />
        <Stack
          direction="row"
          mb="40px"
          wrap="wrap"
          justify="center"
          spacing="0px"
        >
          <Item
            picAlignment="start"
            image="/img/icons/zajemci.svg"
            title="Minimalizujte rizika"
            text="Věděli jste, že v Česku je více než 4,6 miliónů nařízených exekucí na zhruba 750 tisíc osob? Kontrola záznamů se opravdu vyplatí."
          />
          <Item
            picAlignment="start"
            image="/img/icons/penize.svg"
            title="Předejděte problémům"
            text="Vždy je dobré vědět, s kým vstupujete do smluvního vztahu. Tahanice či soudní spory by vás nakonec mohly stát vyšší desítky tisíc."
          />
          <Item
            picAlignment="start"
            image="/img/icons/smlouvy.svg"
            title="Rychlé a snadné řešení"
            text="Kromě starostí vám ušetříme také hodiny času strávené procházením veřejnosprávních portálů a databází."
            bottomPad="0px"
          />
        </Stack>
        <Stack justify="center" align="center" mt="40px" mb={["60px", "80px"]}>
          <CustomLink
            variant="button"
            trackEventPayload={{
              "dl.eCat": "cc",
              "dl.eAct": "cc_click",
              "dl.eLab": "landing_cc",
            }}
            to="/provereni-rizikovosti-osob"
          >
            Prověřit osobu
          </CustomLink>
        </Stack>
      </CustomWrapper>
      <CustomWrapper bgColor="ud-grey-100">
        <Box id="moreCC" />
        <SubHead title="Instantní a spolehlivé výsledky" />
        <Stack
          direction={["column", null, "row"]}
          mb={["0px", null, "90px"]}
          justify="space-between"
          align={["center", null, "unset"]}
        >
          <Stack spacing="0px" align="center" w={["100%", "340px"]}>
            <Heading as="h3" variant="h3m" pb="25px">
              Stačí zadat 2 údaje
            </Heading>
            <Box>
              <Stack
                direction="row"
                align="center"
                w="fit-content"
                borderRadius="md"
                boxShadow="md"
                backgroundColor="white"
                py="5px"
                pl="10px"
                pr="12px"
                transform="rotate(-4deg)"
              >
                <IconPerson1 boxSize="22px" fillWith="ud-primary" />
                <Text color="ud-primary">Jméno a příjmení</Text>
              </Stack>
              <Box pt="20px" />
              <Stack
                direction="row"
                align="center"
                w="fit-content"
                borderRadius="md"
                boxShadow="md"
                backgroundColor="white"
                py="5px"
                pl="10px"
                pr="12px"
                transform="rotate(2deg)"
                ml="40px"
              >
                <IconCalendar boxSize="16px" fillWith="ud-primary" />
                <Text color="ud-primary">Datum narození</Text>
              </Stack>
            </Box>
            <Box py="40px">
              <IconDottedArrow fillWith="ud-primary" boxSize="35px" />
            </Box>
            <Heading as="h3" variant="h3m" pb="16px" textAlign="center">
              Údaje ihned prověříme v nejdůležitějších rejstřících
            </Heading>
            <Text variant="medium" textAlign="center" color="ud-black">
              Za pár vteřin a{" "}
              <Text as="span" fontWeight="bold" color="ud-black">
                149 Kč
              </Text>{" "}
              prověříme bezdlužnost a spolehlivost kohokoliv v nejdůležitějších
              rejstřících v Česku nebo na Slovensku.
            </Text>
            <Box py="40px">
              <IconDottedArrow fillWith="ud-primary" boxSize="35px" />
            </Box>
            <Heading as="h3" variant="h3m" pb="16px">
              Report prověřené osoby
            </Heading>
            <Text
              variant="medium"
              textAlign="center"
              color="ud-black"
              pb="40px"
            >
              Vy dostanete přehledný PDF report, kde hned vidíte, jestli má
              dotyčný nějaký vroubek nebo je čistý jako lilie.
            </Text>
            <Stack
              align="center"
              justify="center"
              display={{ base: "none", lg: "flex" }}
            >
              <CustomLink
                variant="button"
                trackEventPayload={{
                  "dl.eCat": "cc",
                  "dl.eAct": "cc_click",
                  "dl.eLab": "landing_cc",
                }}
                to="/provereni-rizikovosti-osob"
              >
                Prověřit osobu
              </CustomLink>
            </Stack>
          </Stack>
          <Stack align="center" pt={[null, "20px"]}>
            <Box
              position="relative"
              w={["260px", "388px"]}
              h={["315px", "471px"]}
            >
              <NextImage
                src="/img/scenes/CheckLanding/cc_report.jpg"
                alt=""
                fill
                style={{ objectFit: "contain" }}
              />
            </Box>
          </Stack>
        </Stack>
        <Stack
          align="center"
          justify="center"
          pt="50px"
          pb="70px"
          display={{ lg: "none" }}
        >
          <CustomLink
            variant="button"
            trackEventPayload={{
              "dl.eCat": "cc",
              "dl.eAct": "cc_click",
              "dl.eLab": "landing_cc",
            }}
            to="/provereni-rizikovosti-osob"
          >
            Prověřit osobu
          </CustomLink>
        </Stack>
      </CustomWrapper>
      <CustomWrapper>
        <SubHead title="Na koho si můžete posvítit?" />
        <Stack
          direction="row"
          mb={{ base: "60px", md: "80px" }}
          wrap="wrap"
          justify="center"
          spacing="0px"
        >
          <Item
            title="Na nájemníka nebo zájemce o bydlení"
            text="Ujistíte se tak, že je spolehlivý a má dobrou platební morálku."
            picAlignment="flex-start"
            icon={<PersonCircle />}
          />
          <Item
            title="Na spolubydlícího"
            text="Je důležité ověřit si, s kým budete bydlet a že nepřestane platit"
            picAlignment="flex-start"
            icon={<PersonCircle />}
          />
          <Item
            title="Na pronajímatele"
            text="Protože i majitel bytu může mít exekuce. Vás jako nájemníka by pak
            mohla překvapit nepříjemná návštěva od exekutora."
            picAlignment="flex-start"
            icon={<PersonCircle />}
            bottomPad="0px"
          />
        </Stack>
      </CustomWrapper>
      <CustomWrapper bgColor="ud-grey-100">
        <Stack
          mt={{ base: "100px", md: "60px" }}
          direction={{ base: "column", md: "column" }}
          justify="space-around"
          align="center"
          mb={{ base: "100px", md: "60px" }}
        >
          <Box maxW={{ md: "275px" }} textAlign="center">
            <Heading as="h2" mb="20px">
              Prověřte sami sebe
            </Heading>
            <Text variant="medium" mb={{ base: "40px", md: "0px" }}>
              S prověřeným profilem vyniknete mezi ostatními zájemci. Zvýšíte
              tak šanci, že si majitel či makléř vybere právě vás.
            </Text>
          </Box>
          <Box pt="75px">
            <Stack
              w="247px"
              minH="150px"
              borderRadius="6px"
              boxShadow="md"
              bgColor="white"
              position="relative"
              align="center"
              spacing="0px"
            >
              <Text
                variant="large"
                fontWeight="bold"
                color="ud-primary"
                pt="47px"
                textAlign="center"
              >
                {!user?.firstName && !user?.lastName
                  ? "Lucie Spolehlivá"
                  : user.firstName + " " + user.lastName}
              </Text>
              <Text variant="medium" pb="8px">
                {user?.email || "lucie.spolehliva@email.cz"}
              </Text>
              <UserBadge type="checked" />
              <Box pt="33px" />
              <Box position="absolute" top="-50px">
                <NextImage
                  src={user?.photoPath || "/img/scenes/CheckLanding/woman.jpg"}
                  alt=""
                  width="90"
                  height="90"
                  style={{
                    borderRadius: "100%",
                  }}
                />
              </Box>
            </Stack>

            <CustomLink
              w="fit-content"
              display="block"
              m="40px auto 0 auto"
              variant="button"
              trackEventPayload={{
                "dl.eCat": "cc",
                "dl.eAct": "scc_click",
                "dl.eLab": "landing_cc",
              }}
              to="/sebeprovereni"
            >
              Chci sebeprověření
            </CustomLink>
          </Box>
        </Stack>
      </CustomWrapper>
      <CustomWrapper>
        <SubHead title="Věří nám profesionálové i soukromí pronajímatelé" />
        <Referral
          text={[
            "Každý měsíc vystavuji na inzertní portály několik nabídek, na které se mi hlásí desítky zájemců. Díky prověření rizikovosti osob od UlovDomov.cz dokážu rychle odfiltrovat ty, kteří mají dluhy nebo exekuce a nemusím procházet rejstřík po rejstříku. Skvělá úspora času.",
          ]}
          name="Zuzana R."
          relationship=" realitní makléřka"
        />
        <Stack direction="row" mt="52px" mb="40px" wrap="wrap" justify="center">
          <NextImage
            src="/img/common/logos/bravis.png"
            alt="Bravis logo"
            width="124"
            height="50"
            style={{ objectFit: "contain", borderRadius: "6px" }}
          />
          <NextImage
            src="/img/common/logos/century-21.png"
            alt="Century 21 logo"
            width="124"
            height="50"
            style={{ objectFit: "contain", borderRadius: "6px" }}
          />
          <NextImage
            src="/img/common/logos/next.png"
            alt="Next reality logo"
            width="124"
            height="50"
            style={{ objectFit: "contain", borderRadius: "6px" }}
          />
          <NextImage
            src="/img/common/logos/real-spektrum.png"
            alt="Real Spektrum logo"
            width="124"
            height="50"
            style={{ objectFit: "contain", borderRadius: "6px" }}
          />
        </Stack>
        <Stack justify="center" align="center" pb="40px">
          <CustomLink
            variant="button"
            trackEventPayload={{
              "dl.eCat": "cc",
              "dl.eAct": "cc_click",
              "dl.eLab": "landing_cc",
            }}
            to="/provereni-rizikovosti-osob"
          >
            Prověřit osobu
          </CustomLink>
        </Stack>
        <Box id="6regs" />
        <SubHead
          title="Nejdůležitější české a slovenské rejstříky"
          sub="Zadané údaje prověřujeme v nejdůležitějších rejstřících v Česku i na Slovensku, abyste měli jistotu, že víte s kým jednáte."
          customBottom={{ base: "10px", md: "40px" }}
        />
        <ArrowList items={CCS} />
        <SubHead
          title="Máte otázky?"
          customBottom={{ base: "10px", md: "40px" }}
        />
        <ArrowList noIcon arrowColor="ud-primary" items={FAQS} />
        <Box mt={{ "80px": "100px" }} />
      </CustomWrapper>
    </>
  );
};

export default CheckLanding;
